/**
 * Contacts module — per-child list of doctors/schools/etc. with add + delete.
 */
import { useState } from 'react';
import type { Contact, ContactType, CreateContactInput } from '@solandia/core';
import { createContactInputSchema } from '@solandia/core';
import { useI18n } from '../../i18n';
import { useApi } from '../../api/ApiProvider';
import { useFamily } from '../../family/FamilyContext';
import { useAsync } from '../../hooks/useAsync';
import {
  Badge,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Form,
  SectionHeader,
  Spinner,
} from '../../components/ui';

const TYPES: ContactType[] = ['doctor', 'school', 'dentist', 'activity', 'other'];

export function ContactsModule() {
  const { t } = useI18n();
  const api = useApi();
  const { family, activeChild } = useFamily();
  const childId = activeChild!.id;

  const { data, loading, error, reload } = useAsync<Contact[]>(async () => {
    const res = await api.listContacts(family.id, childId);
    return res.items;
  }, [family.id, childId]);

  const [adding, setAdding] = useState(false);

  async function remove(id: string) {
    if (!window.confirm(t.common.confirmDelete)) return;
    await api.deleteContact(family.id, childId, id);
    reload();
  }

  return (
    <section>
      <SectionHeader
        title={t.contacts.title}
        action={
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>
            ＋ {t.contacts.add}
          </button>
        }
      />

      {loading ? <Spinner /> : null}
      {error ? <ErrorState message={t.errors.generic} onRetry={reload} /> : null}

      {data && data.length === 0 ? (
        <EmptyState emoji="📇" title={t.contacts.empty} />
      ) : null}

      {data && data.length > 0 ? (
        <ul className="list">
          {data.map((c) => (
            <li key={c.id} className="list-item">
              <div className="grow stack-sm">
                <div className="row wrap">
                  <strong>{c.label}</strong>
                  <Badge tone="primary">{t.contacts.types[c.type]}</Badge>
                </div>
                {c.phone ? (
                  <a href={`tel:${c.phone}`} className="text-sm">
                    {c.phone}
                  </a>
                ) : null}
                {c.address ? <span className="text-sm muted">{c.address}</span> : null}
                {c.note ? <span className="text-sm">{c.note}</span> : null}
              </div>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => remove(c.id)}
                aria-label={`${t.common.delete} ${c.label}`}
              >
                {t.common.delete}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {adding ? (
        <AddContactDialog
          onClose={() => setAdding(false)}
          onCreated={() => {
            setAdding(false);
            reload();
          }}
        />
      ) : null}
    </section>
  );
}

function AddContactDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useI18n();
  const api = useApi();
  const { family, activeChild } = useFamily();
  const [type, setType] = useState<ContactType>('doctor');
  const [label, setLabel] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function submit() {
    const input: CreateContactInput = {
      type,
      label: label.trim(),
      phone: phone.trim() || null,
      address: address.trim() || null,
      note: note.trim() || null,
    };
    const parsed = createContactInputSchema.safeParse(input);
    if (!parsed.success) {
      setErrors({ label: t.common.required });
      return;
    }
    setBusy(true);
    try {
      await api.createContact(family.id, activeChild!.id, parsed.data);
      onCreated();
    } catch {
      setErrors({ label: t.errors.generic });
      setBusy(false);
    }
  }

  return (
    <Dialog title={t.contacts.add} onClose={onClose} closeLabel={t.common.close}>
      <Form onSubmit={submit}>
        <Field label={t.contacts.typeLabel}>
          {({ id }) => (
            <select
              id={id}
              className="select"
              value={type}
              onChange={(e) => setType(e.target.value as ContactType)}
            >
              {TYPES.map((ty) => (
                <option key={ty} value={ty}>
                  {t.contacts.types[ty]}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label={t.contacts.labelLabel} error={errors.label}>
          {({ id, invalid }) => (
            <input
              id={id}
              className="input"
              value={label}
              aria-invalid={invalid}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t.contacts.labelPlaceholder}
              autoFocus
            />
          )}
        </Field>
        <Field label={`${t.contacts.phoneLabel} (${t.common.optional})`}>
          {({ id }) => (
            <input
              id={id}
              className="input"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          )}
        </Field>
        <Field label={`${t.contacts.addressLabel} (${t.common.optional})`}>
          {({ id }) => (
            <input
              id={id}
              className="input"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          )}
        </Field>
        <Field label={`${t.common.note} (${t.common.optional})`}>
          {({ id }) => (
            <textarea
              id={id}
              className="textarea"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          )}
        </Field>
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t.common.cancel}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? t.common.saving : t.common.add}
          </button>
        </div>
      </Form>
    </Dialog>
  );
}
